import React, { Component } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import {
    Card,
    Row,
    Col,
    Button,
    Space,
    message,
    Spin,
    Typography,
    Divider,
} from "antd";
import {
    PlayCircleOutlined,
    FolderOpenOutlined,
    SettingOutlined,
} from "@ant-design/icons";
import ReplayList from "./components/ReplayList/ReplayList";
import ReplayPlayer from "./components/ReplayPlayer/ReplayPlayer";
import ReplaySettings from "./components/ReplaySettings/ReplaySettings";
import "./ReplayManagement.scss";

// module
import { livereplayList, livereplayDelete } from "../../modules/livereplay";

const { Title } = Typography;

class ReplayManagement extends Component {
    constructor(props) {
        super(props);
        this.state = {
            replays: [],
            loading: false,
            selectedReplay: null,
            showUpload: false,
            showSettings: false,
            filterOptions: {
                search: "",
                cast: null,
                startDate: null,
                endDate: null,
            },
            sortBy: "date",
            sortOrder: "desc",
            pagination: {
                current: 1,
                pageSize: 10,
                total: 0,
            },
        };
    }

    componentDidMount() {
        this.loadReplays();
    }

    loadReplays = async (filters = {}, page = 1, perPage = 10) => {
        this.setState({ loading: true });
        try {
            const params = {
                ...this.state.filterOptions,
                ...filters,
                sortBy: this.state.sortBy,
                sortOrder: this.state.sortOrder,
                page: page,
                perPage: perPage,
            };

            // remove params null value
            Object.keys(params).forEach((key) => {
                if (
                    params[key] === null ||
                    params[key] === "" ||
                    params[key] === undefined
                ) {
                    delete params[key];
                }
            });

            const response = await this.props.livereplayList(params);

            // Handle different response structures
            let replaysData = [];
            let totalCount = 0;

            if (response) {
                // Case 1: Response is array with results and total
                if (
                    Array.isArray(response) &&
                    response.length > 0 &&
                    response[0]
                ) {
                    replaysData = response[0].results || response[0].data || [];
                    totalCount = response[0].total || response[0].count || 0;
                }
                // Case 2: Response has direct results property
                else if (response.results) {
                    replaysData = response.results;
                    totalCount = response.total || response.count || 0;
                }
                // Case 3: Response has data property
                else if (response.data) {
                    if (Array.isArray(response.data)) {
                        replaysData = response.data;
                        totalCount =
                            response.total ||
                            response.count ||
                            response.data.length;
                    } else if (response.data.results) {
                        replaysData = response.data.results;
                        totalCount =
                            response.data.total || response.data.count || 0;
                    }
                }
                // Case 4: Response is direct array
                else if (Array.isArray(response)) {
                    replaysData = response;
                    totalCount = response.length;
                }
            }

            this.setState({
                replays: replaysData,
                pagination: {
                    ...this.state.pagination,
                    current: page,
                    pageSize: perPage,
                    total: totalCount,
                },
                loading: false,
            });
        } catch (error) {
            console.error("Failed to load replays:", error);
            message.error("Failed to load replays");
            this.setState({
                loading: false,
                replays: [],
                pagination: {
                    ...this.state.pagination,
                    total: 0,
                },
            });
        }
    };

    handleReplaySelect = (replay) => {
        this.setState({ selectedReplay: replay });
    };

    handleReplayDelete = async (replayId) => {
        try {
            // Simulate API call
            await this.props.livereplayDelete(replayId);

            // Reload current page data
            const { current, pageSize } = this.state.pagination;
            await this.loadReplays(this.state.filterOptions, current, pageSize);

            // Clear selection if deleted replay was selected
            if (this.state.selectedReplay?.contentId === replayId) {
                this.setState({ selectedReplay: null });
            }

            message.success("Replay deleted successfully");
        } catch (error) {
            console.error("Failed to delete replay:", error);
            message.error("Failed to delete replay");
        }
    };

    handleUploadSuccess = (newReplay) => {
        // Reload first page to show new replay
        this.loadReplays(
            this.state.filterOptions,
            1,
            this.state.pagination.pageSize
        );
        this.setState({ showUpload: false });
        message.success("Replay uploaded successfully");
    };

    handleFilterChange = (
        filterOptions,
        page = 1,
        perPage = this.state.pagination.pageSize
    ) => {

        this.setState({
            filterOptions,
            pagination: {
                ...this.state.pagination,
                current: page,
                pageSize: perPage,
            },
        });
        this.loadReplays(filterOptions, page, perPage);
    };

    handleSortChange = (sortBy, sortOrder) => {
        this.setState(
            {
                sortBy,
                sortOrder,
            },
            () => {
                // Reload current page with new sort
                const { current, pageSize } = this.state.pagination;
                this.loadReplays(this.state.filterOptions, current, pageSize);
            }
        );
    };

    render() {
        const {
            replays,
            loading,
            selectedReplay,
            showUpload,
            showSettings,
            filterOptions,
            sortBy,
            sortOrder,
            pagination,
        } = this.state;

        return (
            <div className="replay-management">
                <div className="management-header">
                    <div className="header-left">
                        <h2>Replay Management</h2>
                        <p>Browse recorded streams and manage replay content</p>
                    </div>
                    <Space>
                        {/* <Button 
							type="primary" 
							icon={<UploadOutlined />}
							onClick={() => this.setState({ showUpload: true })}
						>
							Upload Replay
						</Button>
						<Button 
							icon={<SettingOutlined />}
							onClick={() => this.setState({ showSettings: true })}
						>
							Settings
						</Button> */}
                    </Space>
                </div>

                <Row gutter={[24, 24]} className="management-content">
                    <Col xs={24} lg={selectedReplay ? 14 : 24}>
                        <Card
                            title={
                                <Space>
                                    <FolderOpenOutlined />
                                    Replay Library
                                    <span
                                        style={{
                                            color: "#666",
                                            fontSize: "12px",
                                        }}
                                    >
                                        ({replays.length} of {pagination.total})
                                    </span>
                                </Space>
                            }
                            className="replay-list-card"
                        >
                            <Spin spinning={loading}>
                                <ReplayList
                                    replays={replays}
                                    loading={loading}
                                    selectedReplay={selectedReplay}
                                    filterOptions={filterOptions}
                                    sortBy={sortBy}
                                    sortOrder={sortOrder}
                                    total={pagination.total}
                                    onReplaySelect={this.handleReplaySelect}
                                    onReplayDelete={this.handleReplayDelete}
                                    onFilterChange={this.handleFilterChange}
                                    onSortChange={this.handleSortChange}
                                />
                            </Spin>
                        </Card>
                    </Col>

                    {selectedReplay && (
                        <Col xs={24} lg={10}>
                            <ReplayPlayer
                                replay={selectedReplay}
                                onClose={() =>
                                    this.setState({ selectedReplay: null })
                                }
                            />
                        </Col>
                    )}
                </Row>

                {/* Upload Modal */}
                {/* {showUpload && (
					<ReplayUpload
						visible={showUpload}
						onCancel={() => this.setState({ showUpload: false })}
						onSuccess={this.handleUploadSuccess}
					/>
				)} */}

                {/* Settings Modal */}
                {/* {showSettings && (
					<ReplaySettings
						visible={showSettings}
						onCancel={() => this.setState({ showSettings: false })}
						onSave={(settings) => {
							console.log('Settings saved:', settings);
							this.setState({ showSettings: false });
							message.success('Settings saved successfully');
						}}
					/>
				)} */}
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    // Add any state mappings here
});

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            livereplayList,
            livereplayDelete,
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(ReplayManagement);
